import React, { useRef, useState } from 'react'
import Lottie, { LottieComponentProps } from 'lottie-react'
import { createPopper } from '@popperjs/core'

type LottieOption = {
  loop: boolean
  autoPlay: boolean
  animationData: object
  width: number
  height: number
  rendererSettings: {
    preserveAspectRatio: string
  }
}

type MobileIconButtonProps = {
  badge?: string | number
  onClick?: (e: any) => void
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement> & { fillcolor?: string }>
  isFocused?: boolean
  bgColor?: string
  disabledOpacity?: number
  focusIconColor?: string
  disabled?: boolean
  large?: boolean
  tooltipTitle?: string
  btnId?: string
  buttonText?: string
  lottieOption?: LottieOption | null
}

export const MobileIconButton: React.FC<MobileIconButtonProps> = ({
  badge,
  onClick,
  Icon,
  isFocused = false,
  bgColor,
  disabledOpacity = 0.7,
  focusIconColor = '#fff',
  disabled = false,
  large = false,
  tooltipTitle,
  btnId,
  buttonText,
  lottieOption,
}) => {
  const [mouseOver, setMouseOver] = useState(false)
  const [mouseDown, setMouseDown] = useState(false)
  const [tooltipShow, setTooltipShow] = useState(false)
  const btnRef = useRef<HTMLDivElement | null>(null)
  const tooltipRef = useRef<HTMLDivElement | null>(null)

  const openTooltip = () => {
    if (btnRef.current && tooltipRef.current) {
      createPopper(btnRef.current, tooltipRef.current, {
        placement: 'bottom',
      })
      setTooltipShow(true)
    }
  }

  const closeTooltip = () => setTooltipShow(false)

  const iconSize = 24 * (large ? 1.7 : 1)

  return (
    <div className="flex-grow mr-2 mt-2">
      <div
        ref={btnRef}
        onMouseEnter={openTooltip}
        onMouseLeave={closeTooltip}
        className="p-1 w-full w-[100px] h-[75px] flex justify-center rounded-lg border"
        style={{
          transition: `all 100ms`,
          transitionTimingFunction: 'linear',
        }}
      >
        <button
          className="flex flex-col items-center justify-center w-full h-full"
          id={btnId}
          onMouseEnter={() => setMouseOver(true)}
          onMouseLeave={() => setMouseOver(false)}
          onMouseDown={() => setMouseDown(true)}
          onMouseUp={() => setMouseDown(false)}
          disabled={disabled}
          onClick={onClick}
        >
          <div
            className="p-1 relative flex items-center justify-center rounded-lg"
            style={{
              opacity: disabled ? disabledOpacity : 1,
              transform: `scale(${mouseOver ? (mouseDown ? 0.95 : 1.1) : 1})`,
              transition: `all 100ms`,
              transitionTimingFunction: 'linear',
            }}
          >
            {badge && (
              <div
                className="inline-flex absolute -top-2 -right-3 justify-center items-center w-6 h-6 text-xs font-bold text-white bg-black rounded-full"
                style={{ color: isFocused ? focusIconColor : 'white' }}
              >
                {badge}
              </div>
            )}
            {lottieOption ? (
              <div
                className="flex items-center justify-center p-[5px] rounded-[5px]"
                style={{ backgroundColor: bgColor }}
              >
                <div
                  style={{
                    height: iconSize,
                    width: (iconSize * lottieOption.width) / lottieOption.height,
                  }}
                >
                  <Lottie
                    loop={lottieOption.loop}
                    autoPlay={lottieOption.autoPlay}
                    animationData={lottieOption.animationData}
                    rendererSettings={{
                      preserveAspectRatio: lottieOption.rendererSettings.preserveAspectRatio,
                    }}
                  />
                </div>
              </div>
            ) : (
              <Icon
                style={{
                  color: isFocused ? focusIconColor : '#95959E',
                  height: iconSize,
                  width: iconSize,
                }}
                fillcolor={isFocused ? focusIconColor : '#95959E'}
              />
            )}
          </div>
          {buttonText && <p className="dark:text-white text-sm">{buttonText}</p>}
        </button>
      </div>
      <div
        style={{ zIndex: 999 }}
        className={`${
          tooltipShow ? '' : 'hidden'
        } overflow-hidden flex flex-col items-center justify-center pb-1`}
        ref={tooltipRef}
      >
        <div className="rounded-md p-1.5 bg-black">
          <p className="text-base text-white">{tooltipTitle || ''}</p>
        </div>
      </div>
    </div>
  )
}
